import type {Component, Editor} from 'grapesjs';
import {GA4EventParamName} from '@Libraries/ga-events';
import {notifyPuppeteer} from '@Libraries/puppeeter.library';
import {noop} from '@Utils/general.util';
import type {EmailMakerImmutableStore} from '@EmailMakerReact/email-maker-react.types';
import {EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {getMainWrapper, getStringProp} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {getInlinedHtml} from '@EmailMakerReact/lib/email-maker-html-export-library';
import {reloadAllDesigns, reloadPremiumBackgrounds} from '@EmailMakerReact/lib/email-maker-design-library';
import {warnIfGettyLimitsExceeded} from '@EmailMakerReact/lib/email-maker-getty-library';
import {getCurrentIdEmailTemplate} from '@EmailMakerReact/lib/email-maker-storage-library';

/** Miscellaneous editor utilities for the React email maker. */

/** `@Libraries/auto-branding-email-maker` dispatches this event after a palette is stored. */
const PALETTE_APPLIED_EVENT = 'custom:palette:applied';
/** The DOM marker the puppeteer driver polls for. */
const PUPPETEER_NOTIFIER_ID = 'editor-execution-complete';
const PUPPETEER_AUTO_BRANDING_FLOW = 'TEMPLATE_AUTO_BRANDING';

/** Fires a GA4 event carrying the component's tracking name as the type param. */
export function trackGaEventForComponent(editor: Editor, eventAction: string, component?: Component): void {
  const target = component ?? editor.getSelected();
  const trackingName = target ? getStringProp(target, 'trackingName') : undefined;

  window.PMW.gtm.trackGA4CustomEvent(eventAction, {[GA4EventParamName.TYPE]: trackingName});
}

/**
 * In the puppeteer auto-branding flow, persists the recolored template and signals the driver
 * once a palette is applied. Returns a teardown the editor-destroy path must call.
 */
export function addPuppeteerListeners(editor: Editor, store: EmailMakerImmutableStore): () => void {
  if (store.puppeteerConfig?.flow !== PUPPETEER_AUTO_BRANDING_FLOW) {
    return noop;
  }

  const onPaletteApplied = (): void => {
    notifyPuppeteerPostAutoBranding(editor);
  };

  window.addEventListener(PALETTE_APPLIED_EVENT, onPaletteApplied);
  return (): void => {
    window.removeEventListener(PALETTE_APPLIED_EVENT, onPaletteApplied);
  };
}

/** Shows queued marketing popups once the editor has loaded. */
export function showPendingMarketingPopups(): void {
  if (typeof window.PMW.marketing !== 'undefined') {
    window.PMW.marketing.showPendingMarketingPopups();
  }
}

/**
 * Re-applies the freshest template name (which may have changed in the DB after the JSON snapshot
 * was cached) onto the wrapper, then re-toggles the selection so dependent UI re-reads it.
 */
export function syncTemplateNameFromStore(editor: Editor, store: EmailMakerImmutableStore): void {
  const selected = editor.getSelected();

  const wrapper = getMainWrapper(editor);
  if (wrapper) {
    const currentName = getStringProp(wrapper, EMAIL_MAKER_PROPS.TEMPLATE_NAME);
    if (store.templateName !== currentName) {
      wrapper.set(EMAIL_MAKER_PROPS.TEMPLATE_NAME, store.templateName);
    }
  }

  if (selected) {
    editor.selectToggle(selected);
    editor.selectToggle(selected);
  }
}

/** Once the content has mounted, re-resolves premium backgrounds and design previews outside the undo history. */
export function onAllComponentsLoaded(editor: Editor): void {
  editor.getModel().skip(() => {
    reloadPremiumBackgrounds(editor);
    reloadAllDesigns(editor);
  });
  warnIfGettyLimitsExceeded(editor);
}

/** Saves the recolored HTML+JSON for the bypassed template, then marks execution complete for the puppeteer driver. */
function notifyPuppeteerPostAutoBranding(editor: Editor): void {
  const htmlContent = getInlinedHtml(editor);
  const jsonContent = JSON.stringify(editor.getProjectData());

  void window.PMW.writeLocal('emailmaker/saveTemplateDataForPuppeteerAutoBranding', {
    htmlContent,
    jsonContent,
    idEmailTemplate: getCurrentIdEmailTemplate(editor),
  });

  notifyPuppeteer(PUPPETEER_NOTIFIER_ID);
}
